import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdDashboard } from "react-icons/md";
import {
  FaArrowLeft,
  FaArrowRight,
  FaPills,
  FaShoppingCart,
  FaUsers,
  FaDollarSign,
  FaUserMd,
  FaSignOutAlt,
  FaMoon,
  FaSun
} from 'react-icons/fa';
import { motion } from 'framer-motion';

// Import BASE_URL from your backend configuration
import { BASE_URL } from "../backend/backend.js";

const AdminSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => setIsOpen(!isOpen);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('isDarkMode', JSON.stringify(newMode));
    document.body.style.backgroundColor = newMode ? '#2c3e50' : '#ecf0f1';
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('isDarkMode');
    if (savedTheme !== null) {
      setIsDarkMode(JSON.parse(savedTheme));
      document.body.style.backgroundColor = JSON.parse(savedTheme) ? '#2c3e50' : '#ecf0f1';
    }

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Replaced hardcoded URL with BASE_URL
    fetch(`${BASE_URL}/user/loggeduser`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(response => response.json())
      .then(data => setUser(data))
      .catch(error => console.error('Error fetching user:', error));
  }, []);

  const handleLogout = () => {
    // Replaced hardcoded URL with BASE_URL
    fetch(`${BASE_URL}/logout`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(response => {
        if (response.ok) {
          navigate('/');
        } else {
          console.error('Logout failed');
        }
      })
      .catch(error => {
        console.error('Logout error:', error);
      });
  };

  const sidebarStyle = {
    height: '100vh',
    width: isOpen ? (isMobile ? '100%' : '300px') : '60px',
    backgroundColor: isDarkMode ? '#34495e' : '#ecf0f1',
    color: isDarkMode ? '#ecf0f1' : '#34495e',
    padding: isOpen ? '20px 15px' : '15px 5px',
    overflowY: 'auto',
    transition: 'width 0.4s ease-in-out, padding 0.4s ease-in-out, transform 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    position: isMobile ? 'fixed' : 'relative',
    zIndex: 1000,
    boxShadow: isOpen ? '2px 0 15px rgba(0, 0, 0, 0.1)' : 'none',
    alignItems: isOpen ? 'flex-start' : 'center',
    bottom: isMobile ? 0 : 'auto',
    left: isMobile ? 0 : 'auto',
    right: isMobile ? 0 : 'auto',
    transform: isMobile && !isOpen ? 'translateY(100%)' : 'translateY(0)',
  };

  const horizontalSidebarStyle = {
    backgroundColor: isDarkMode ? '#34495e' : '#ecf0f1',
    color: isDarkMode ? '#ecf0f1' : '#34495e',
    padding: '15px',
    transition: 'transform 0.3s ease-in-out',
    zIndex: 1000,
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    height: 'auto',
  };

  const modeButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: !isMobile || isOpen ? '15px' : 0,
    cursor: 'pointer',
    transition: 'transform 0.3s ease-in-out',
    justifyContent: !isMobile || isOpen ? 'flex-start' : 'center',
  };

  const iconStyle = {
    marginRight: !isMobile || isOpen ? '15px' : '0px',
    fontSize: '1.5em',
    transition: 'margin 0.3s ease-in-out',
  };

  const logoContainerStyle = {
    marginBottom: !isMobile || isOpen ? '30px' : 0,
    textAlign: !isMobile || isOpen ? 'center' : 'center',
    transition: 'opacity 0.3s ease, height 0.3s ease, margin-bottom 0.3s ease',
    opacity: 1,
    height: 'auto',
    overflow: 'hidden',
    display: isMobile && !isOpen ? 'none' : 'block',
  };

  const logoTextStyle = {
    fontSize: !isMobile || isOpen ? '24px' : '1.8em',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    transition: 'font-size 0.3s ease-in-out',
    display: 'inline-block',
  };

  const logoLetterGStyle = {
    display: 'inline-block',
    transition: 'transform 4s ease-in-out',
    transform: isDarkMode ? 'rotate(2880deg)' : 'rotate(0deg)',
  };

  const userDetailStyle = {
    fontSize: '16px',
    color: '#bdc3c7',
    marginTop: '5px',
    display: !isMobile || isOpen ? 'block' : 'none',
  };

  const logoutButtonStyle = {
    marginTop: !isMobile || isOpen ? 'auto' : 0,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#e74c3c',
    padding: !isMobile || isOpen ? '12px 20px' : '10px',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: !isMobile || isOpen ? '16px' : '1.2em',
    justifyContent: !isMobile || isOpen ? 'flex-start' : 'center',
    transition: 'background-color 0.3s ease, font-size 0.3s ease, padding 0.3s ease, margin-top 0.3s ease',
  };

  const toggleSwitchStyle = {
    position: 'relative',
    width: '60px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'space-between',
    backgroundColor: isDarkMode ? '#34495e' : '#ecf0f1',
    borderRadius: '30px',
    boxShadow: isDarkMode ? '0 0 10px rgba(0, 0, 0, 0.5)' : '0 0 10px rgba(200, 200, 200, 0.5)',
  };

  const sliderStyle = {
    position: 'absolute',
    top: '50%',
    left: isDarkMode ? '30px' : '0',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    backgroundColor: isDarkMode ? '#ecf0f1' : '#34495e',
    borderRadius: '50%',
    transition: 'left 0.3s ease, background-color 0.3s ease',
    boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
  };

  const toggleTextStyle = {
    marginLeft: '10px',
    fontSize: '16px',
    color: isDarkMode ? '#ecf0f1' : '#34495e',
    transition: 'color 0.3s ease-in-out',
    display: !isMobile || isOpen ? 'inline-block' : 'none',
  };

  const linkButtonStyle = {
    fontSize: !isMobile || isOpen ? '18px' : '1.5em',
    color: isDarkMode ? '#ecf0f1' : '#34495e',
    textDecoration: 'none',
    padding: !isMobile || isOpen ? '12px 15px' : '10px',
    borderRadius: '8px',
    marginBottom: !isMobile || isOpen ? '10px' : 0,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: !isMobile || isOpen ? 'flex-start' : 'center',
    transition: 'background-color 0.3s ease, color 0.3s ease, padding 0.3s ease, font-size 0.3s ease, margin-bottom 0.3s ease',
  };

  const mobileLinkButtonStyle = {
    fontSize: '1.2em',
    color: isDarkMode ? '#ecf0f1' : '#34495e',
    textDecoration: 'none',
    padding: '10px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease, color 0.3s ease',
  };

  const mobileLogoutButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#e74c3c',
    padding: '10px 12px',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1.2em',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease, font-size 0.3s ease, padding 0.3s ease',
  };

  const mobileToggleButtonStyle = {
    position: 'fixed',
    top: '10px',
    left: '10px',
    zIndex: 1100,
    padding: '8px 10px',
    backgroundColor: isDarkMode ? '#ecf0f1' : '#34495e',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: isDarkMode ? '#34495e' : '#ecf0f1',
  };

  return (
    <>
      {isMobile ? (
        <>
          <button onClick={toggleSidebar} style={mobileToggleButtonStyle}>
            {isOpen ? <FaArrowLeft /> : <FaArrowRight />}
          </button>
          <motion.div
            style={{
              ...horizontalSidebarStyle,
              transform: !isOpen ? 'translateY(0)' : 'translateY(100%)',
              display: !isOpen ? 'flex' : 'none',
            }}
            initial={{ y: 0 }}
            animate={{ y: !isOpen ? 0 : '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <Link to="/ad" style={mobileLinkButtonStyle}><MdDashboard style={iconStyle} /></Link>
            <Link to="/mm" style={mobileLinkButtonStyle}><FaPills style={iconStyle} /></Link>
            <Link to="/om" style={mobileLinkButtonStyle}><FaShoppingCart style={iconStyle} /></Link>
            <Link to="/um" style={mobileLinkButtonStyle}><FaUsers style={iconStyle} /></Link>
            <Link to="/sm" style={mobileLinkButtonStyle}><FaDollarSign style={iconStyle} /></Link>
            <Link to="/dm" style={mobileLinkButtonStyle}><FaUserMd style={iconStyle} /></Link>
            <Link to="/mb" style={mobileLinkButtonStyle}><FaUserMd style={iconStyle} /></Link>
            <button onClick={handleLogout} style={mobileLogoutButtonStyle}>
              <FaSignOutAlt style={{ marginRight: '5px' }} />
            </button>
          </motion.div>
          <motion.div
            style={{
              ...sidebarStyle,
              display: isOpen ? 'flex' : 'none',
              width: '250px',
            }}
            initial={{ x: -250 }}
            animate={{ x: isOpen ? 0 : -250 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <motion.div
              style={logoContainerStyle}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <br/><br/><br/><br/>
              <div style={logoTextStyle}>
                <span style={logoLetterGStyle}>G</span>
                {isOpen && (
                  <>
                    <span style={logoLetterGStyle}>i</span>rraj
                    <span style={logoLetterGStyle}>M</span>
                    <span style={logoLetterGStyle}>e</span>dico
                  </>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px' }}>
                {user && (
                  <>
                    <div style={userDetailStyle}>👤 {user.username}</div>
                    <div style={userDetailStyle}>🔐 Role: {user.roles[0]?.name}</div>
                  </>
                )}
              </div>
            </motion.div>

            <div style={modeButtonStyle}>
              <label htmlFor="darkModeToggle" style={toggleSwitchStyle}>
                <input
                  type="checkbox"
                  id="darkModeToggle"
                  checked={isDarkMode}
                  onChange={toggleDarkMode}
                  style={{ display: 'none' }}
                />
                <span style={sliderStyle}></span>
              </label>
              <span style={toggleTextStyle}>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </div>

            <Link to="/ad" style={linkButtonStyle}><MdDashboard style={iconStyle} /> {isOpen && 'Dashboard'}</Link>
            <Link to="/mm" style={linkButtonStyle}><FaPills style={iconStyle} /> {isOpen && 'Medicine Detail'}</Link>
            <Link to="/om" style={linkButtonStyle}><FaShoppingCart style={iconStyle} /> {isOpen && 'Total Order'}</Link>
            <Link to="/um" style={linkButtonStyle}><FaUsers style={iconStyle} /> {isOpen && 'Total User'}</Link>
            <Link to="/sm" style={linkButtonStyle}><FaDollarSign style={iconStyle} /> {isOpen && 'Total Sell'}</Link>
            <Link to="/dm" style={linkButtonStyle}><FaUserMd style={iconStyle} /> {isOpen && 'Doctor Detail'}</Link>
            <Link to="/mb" style={linkButtonStyle}><FaUserMd style={iconStyle} /> {isOpen && 'Make Bill'}</Link>

            <button onClick={handleLogout} style={logoutButtonStyle}>
              <FaSignOutAlt style={{ marginRight: isOpen ? '10px' : '0' }} /> {isOpen && 'Logout'}
            </button>
          </motion.div>
        </>
      ) : (
        <div style={sidebarStyle}>
          <motion.div
            style={logoContainerStyle}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div style={logoTextStyle}>
              <span style={logoLetterGStyle}>G</span>
              {isOpen && (
                <>
                  <span style={logoLetterGStyle}>i</span>rraj
                  <span style={logoLetterGStyle}>M</span>
                  <span style={logoLetterGStyle}>e</span>dico
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop:'10px' }}>
              {user && (
                <>
                  <div style={userDetailStyle}>👤 {user.username}</div>
                  <div style={userDetailStyle}>🔐 Role: {user.roles[0]?.name}</div>
                </>
              )}
            </div>
          </motion.div>

          <div style={modeButtonStyle}>
            <label htmlFor="darkModeToggle" style={toggleSwitchStyle}>
              <input
                type="checkbox"
                id="darkModeToggle"
                checked={isDarkMode}
                onChange={toggleDarkMode}
                style={{ display: 'none' }}
              />
              <span style={sliderStyle}></span>
            </label>
            <span style={toggleTextStyle}>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </div>

          <Link to="/ad" style={linkButtonStyle}><MdDashboard style={iconStyle} /> {isOpen && 'Dashboard'}</Link>
          <Link to="/mm" style={linkButtonStyle}><FaPills style={iconStyle} /> {isOpen && 'Medicine Detail'}</Link>
          <Link to="/om" style={linkButtonStyle}><FaShoppingCart style={iconStyle} /> {isOpen && 'Total Order'}</Link>
          <Link to="/um" style={linkButtonStyle}><FaUsers style={iconStyle} /> {isOpen && 'Total User'}</Link>
          <Link to="/sm" style={linkButtonStyle}><FaDollarSign style={iconStyle} /> {isOpen && 'Total Sell'}</Link>
          <Link to="/dm" style={linkButtonStyle}><FaUserMd style={iconStyle} /> {isOpen && 'Doctor Detail'}</Link>
          <Link to="/mb" style={linkButtonStyle}><FaUserMd style={iconStyle} /> {isOpen && 'Make Bill'}</Link>

          <button onClick={handleLogout} style={logoutButtonStyle}>
            <FaSignOutAlt style={{ marginRight: isOpen ? '10px' : '0' }} /> {isOpen && 'Logout'}
          </button>
        </div>
      )}

      {!isMobile && (
        <button
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: '20px',
            left: isOpen ? '280px' : '10px',
            zIndex: 1100,
            transition: 'left 0.3s ease-in-out',
            padding: '10px 12px',
            backgroundColor: isDarkMode ? '#ecf0f1' : '#34495e',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: isDarkMode ? '#34495e' : '#ecf0f1'
          }}
        >
          {isOpen ? <FaArrowLeft /> : <FaArrowRight />}
        </button>
      )}
    </>
  );
};

export default AdminSidebar;